import React, {useCallback, useEffect, useMemo, useState} from "react";
import {Box, Button, createListCollection, Field, Heading, Input, Select, Show, Table, Textarea,} from "@chakra-ui/react"
import {useToast} from "@chakra-ui/toast";
import {CompanyExecutor, dataApi, ExecutorContract, ExecutorTask as ExTask} from "../../api/data-api";
import {DateTime} from "luxon";
import {Tooltip} from "../../components/Tooltip";
import {DocumentFile} from "../Document/documentFile";

export interface ExecutorTaskExt {
    id: number | null,
    name: string,
    type1: string,
    description: string,
    address: string,
    startDate: string,
    endDate: string,
    value: string,
    documentId: number | null,
    category: string,
    project: string,
    comments: string,
    calculationType: "TASK" | "HOUR",
    calculationTimeHEstimated: number,
    calculationTimeHActual: number,
    calculationTimeHVerified: number,
    resultValue: string,
    sended: boolean,
    executorContractId: number | null,
    companyExecutorId: number | null,
}

export const taskTypeList = createListCollection({
    items: [
        {label: "Local", value: "LOCAL"},
        {label: "Remote", value: "REMOTE"},
    ],
});

export default function ExecutorTask(props: {
    contract: ExecutorContract | undefined,
    executor: CompanyExecutor | undefined,
    isCompany: boolean,
    show: boolean
}) {
    const inputDefault: ExecutorTaskExt = useMemo(() => {
        return {
            id: null,
            name: "",
            type1: "LOCAL",
            description: "",
            address: "",
            startDate: "",
            endDate: "",
            value: "100",
            documentId: null,
            category: "",
            project: "",
            comments: "",
            calculationType: "TASK",
            calculationTimeHEstimated: 1,
            calculationTimeHActual: 1,
            calculationTimeHVerified: 1,
            resultValue: "100",
            sended: false,
            executorContractId: null,
            companyExecutorId: null,
        }
    }, []);
    const [inputParams, setInputParams] = useState<ExecutorTaskExt>(inputDefault);
    const [items, setItems] = useState<ExTask[]>([]);
    const toast = useToast();
    const [showForm, setShowForm] = useState<boolean>(false);
    const loadItems = useCallback(() => {
        if (props.contract && props.executor) {
            dataApi.executorTaskList(props.contract.ID, props.executor.COMPANY_ID, 0, 10)
                .then((o) => setItems(o))
                .catch((err) => {
                    console.log("Error status code is: " + err);
                    toast({title: "Error", description: `code: [${err}]`, status: "error"});
                });
        }
    }, [props, toast]);
    useEffect(() => {
        loadItems();
    }, [loadItems, props, toast]);

    const prepareInput = useCallback((item: ExTask | null) => {
        setInputParams({
            id: item ? item.ID : null,
            name: item ? item.NAME : "",
            type1: item ? item.TYPE1 : "LOCAL",
            description: item ? item.DESCRIPTION : "",
            address: item ? item.ADDRESS : "",
            startDate: item ? DateTime.fromMillis(item.START_DATE).toFormat('dd.MM.yyyy') : "",
            endDate: item ? DateTime.fromMillis(item.END_DATE).toFormat('dd.MM.yyyy') : "",
            value: item ? String(item.VALUE1) : "100",
            documentId: item ? item.DOCUMENT_ID : null,
            sended: !!(item && item.SENDED != null),
            category: item ? item.CATEGORY : "",
            project: item ? item.PROJECT : "",
            comments: item ? item.COMMENTS : "",
            resultValue: item ? String(item.RESULT_VALUE) : "100",
            calculationType: item ? item.CALCULATION_TYPE : "TASK",
            calculationTimeHEstimated: item ? item.CALCULATION_TIME_H_ESTIMATED : 1,
            calculationTimeHActual: item ? item.CALCULATION_TIME_H_ACTUAL : 1,
            calculationTimeHVerified: item ? item.CALCULATION_TIME_H_VERIFIED : 1,
            executorContractId: item ? item.EXECUTOR_CONTRACT_ID : null,
            companyExecutorId: item ? item.COMPANY_EXECUTOR_ID : (props.contract ? props.contract.COMPANY_EXECUTOR_ID : null),
        });
    }, [props]);

    const createOrUpdate = useCallback(() => {
        if (!props.contract || !props.contract.ID || !inputParams.companyExecutorId)
            return;
        dataApi.executorTaskCreateOrUpdate(
            inputParams.id, props.contract.ID, props.contract.COMPANY_EXECUTOR_ID, inputParams.type1, inputParams.name, inputParams.description, inputParams.address,
            DateTime.fromFormat(inputParams.startDate, 'dd.MM.yyyy').toMillis(), DateTime.fromFormat(inputParams.endDate, 'dd.MM.yyyy').toMillis(),
            parseFloat(inputParams.value), inputParams.category, inputParams.project, inputParams.comments, inputParams.calculationType, inputParams.calculationTimeHEstimated)
            .then((value) => {
                toast({title: "Save", status: "success"});
                setItems(prev => {
                    const lst = [...prev];
                    if (inputParams.id) {
                        let findO = lst.find(o => o.ID === value.ID);
                        if (findO)
                            lst[lst.indexOf(findO)] = value;
                    } else {
                        lst.push(value);
                    }
                    return lst;
                });
                prepareInput(null);
                setShowForm(false);
            })
            .catch((err) => {
                console.log("Error status code is: " + err);
                toast({title: "Error", description: `code: [${err}]`, status: "error"});
            });
    }, [props.contract, inputParams, toast, prepareInput]);

    const setSend = useCallback((item: ExTask) => {
        if (!item)
            return;
        dataApi.executorTaskSend(item.ID)
            .then((o) => {
                if (o) {
                    toast({title: "Set send", status: "success"});
                    item.SENDED = new Date().getTime();
                    setItems(prev => {
                        const lst = [...prev];
                        let index = lst.indexOf(item);
                        if (index > -1)
                            lst[index] = item;
                        return lst;
                    });
                } else {
                    toast({title: "Set send failed", status: "error"});
                }
            })
            .catch((err) => {
                console.log("Error status code is: " + err);
                toast({title: "Error", description: `code: [${err}]`, status: "error"});
            });
    }, [toast]);

    const setExecute = useCallback((item: ExTask, calculationTimeHActual: number) => {
        if (!item)
            return;
        dataApi.executorTaskExecute(item.ID, calculationTimeHActual)
            .then((o) => {
                if (o) {
                    toast({title: "Set execute", status: "success"});
                    item.EXECUTED = new Date().getTime();
                    setItems(prev => {
                        const lst = [...prev];
                        let index = lst.indexOf(item);
                        if (index > -1)
                            lst[index] = item;
                        return lst;
                    });
                } else {
                    toast({title: "Set execute failed", status: "error"});
                }
            })
            .catch((err) => {
                console.log("Error status code is: " + err);
                toast({title: "Error", description: `code: [${err}]`, status: "error"});
            });
    }, [toast]);

    const setConfirm = useCallback((item: ExTask, calculationTimeHVerified: number) => {
        if (!item)
            return;
        dataApi.executorTaskConfirm(item.ID, calculationTimeHVerified)
            .then((o) => {
                if (o) {
                    toast({title: "Set confirm", status: "success"});
                    item.CONFIRMED = new Date().getTime();
                    setItems(prev => {
                        const lst = [...prev];
                        let index = lst.indexOf(item);
                        if (index > -1)
                            lst[index] = item;
                        return lst;
                    });
                } else {
                    toast({title: "Set confirm failed", status: "error"});
                }
            })
            .catch((err) => {
                console.log("Error status code is: " + err);
                toast({title: "Error", description: `code: [${err}]`, status: "error"});
            });
    }, [toast]);

    const remove = useCallback((item: ExTask) => {
        if (!item)
            return;
        dataApi.executorTaskDelete(item.ID)
            .then((o) => {
                if (o) {
                    toast({title: "Delete", status: "success"});
                    setItems(prev => {
                        return prev.filter(o => o.ID !== item.ID);
                    });
                } else {
                    toast({title: "Delete", status: "error"});
                }
            })
            .catch((err) => {
                console.log("Error status code is: " + err);
                toast({title: "Error", description: `code: [${err}]`, status: "error"});
            });
    }, [toast]);

    return props.show ? (
        <Box borderWidth={1} borderColor="gray">
            <Show when={showForm}>
                <Heading size="xl">Задача {inputParams.id}</Heading>
                <Box borderWidth={1} borderColor="gray">
                    <Field.Root orientation="horizontal">
                        <Field.Label>Имя</Field.Label>
                        <Input type="text" placeholder="Имя"
                               value={inputParams.name}
                               onChange={(it) => setInputParams((prevState) => {
                                   return {...prevState, name: it.target.value}
                               })}/>
                    </Field.Root>
                    <Field.Root orientation="horizontal">
                        <Field.Label>Описание</Field.Label>
                        <Textarea placeholder="Описание"
                                  value={inputParams.description}
                                  onChange={(it) => setInputParams((prev) => {
                                      return {...prev, description: it.target.value}
                                  })}/>
                    </Field.Root>
                    <Field.Root orientation="horizontal" required>
                        <Field.Label>Тип <Field.RequiredIndicator/></Field.Label>
                        <Select.Root
                            value={[inputParams.type1]}
                            // defaultValue={[String(inputParams.sex)]}
                            collection={taskTypeList}
                            onValueChange={(e: any) => setInputParams(prev => {
                                return {
                                    ...prev, type1: e.value[0]
                                }
                            })}>
                            <Select.HiddenSelect/>
                            <Select.Control>
                                <Select.Trigger>
                                    <Select.ValueText placeholder="Тип"/>
                                </Select.Trigger>
                                <Select.IndicatorGroup>
                                    <Select.Indicator/>
                                </Select.IndicatorGroup>
                            </Select.Control>
                            <Select.Positioner>
                                <Select.Content>
                                    {taskTypeList.items.map((it: any) => (
                                        <Select.Item item={it} key={it.value}>
                                            {it.label}
                                            <Select.ItemIndicator/>
                                        </Select.Item>
                                    ))}
                                </Select.Content>
                            </Select.Positioner>
                        </Select.Root>
                    </Field.Root>
                    <Field.Root orientation="horizontal">
                        <Field.Label>Адрес</Field.Label>
                        <Input type="text" placeholder="Адрес"
                               value={inputParams.address}
                               onChange={(it) => setInputParams((prevState) => {
                                   return {...prevState, address: it.target.value}
                               })}/>
                    </Field.Root>
                    <Field.Root orientation="horizontal">
                        <Tooltip showArrow content="date, format dd.mm.yyyy">
                            <Field.Label>Дата начала <Field.RequiredIndicator/></Field.Label>
                        </Tooltip>
                        <Input type="text" placeholder="Дата начала"
                               value={inputParams.startDate}
                               onChange={(it) => setInputParams((prevState) => {
                                   return {...prevState, startDate: it.target.value}
                               })}/>
                    </Field.Root>
                    <Field.Root orientation="horizontal">
                        <Tooltip showArrow content="date, format dd.mm.yyyy">
                            <Field.Label>Дата окончания <Field.RequiredIndicator/></Field.Label>
                        </Tooltip>
                        <Input type="text" placeholder="Дата окончания"
                               value={inputParams.endDate}
                               onChange={(it) => setInputParams((prevState) => {
                                   return {...prevState, endDate: it.target.value}
                               })}/>
                    </Field.Root>
                    <Field.Root orientation="horizontal">
                        <Field.Label>Сумма <Field.RequiredIndicator/></Field.Label>
                        <Input type="number" placeholder="Сумма"
                               value={inputParams.value}
                               onChange={(it) => setInputParams((prevState) => {
                                   return {...prevState, value: it.target.value}
                               })}/>
                    </Field.Root>
                    <Button borderRadius={0} variant="solid" colorScheme="teal" width="full"
                            disabled={inputParams.sended}
                            onClick={e => createOrUpdate()}>
                        {inputParams.id ? "Сохранить" : "Создать"}
                    </Button>
                </Box>
                {inputParams.id && inputParams.documentId ? (<DocumentFile documentId={inputParams.documentId} editable={true}/>) : null}
            </Show>
            <Heading size="xl">Задачи</Heading>
            <Button onClick={e => {
                if (showForm) {
                    setShowForm(false);
                } else {
                    prepareInput(null);
                    setShowForm(true);
                }
            }}>
                Создать новый
            </Button>
            <Table.Root size="sm" variant="outline" striped>
                <Table.Header>
                    <Table.Row>
                        <Table.ColumnHeader>Имя</Table.ColumnHeader>
                        <Table.ColumnHeader>Тип</Table.ColumnHeader>
                        <Table.ColumnHeader>Дата начала</Table.ColumnHeader>
                        <Table.ColumnHeader>Дата окончания</Table.ColumnHeader>
                        <Table.ColumnHeader>Сумма</Table.ColumnHeader>
                        <Table.ColumnHeader>Изменен</Table.ColumnHeader>
                        <Table.ColumnHeader>Отправлен</Table.ColumnHeader>
                        <Table.ColumnHeader>Принят</Table.ColumnHeader>
                        <Table.ColumnHeader>Исполнен</Table.ColumnHeader>
                        <Table.ColumnHeader>Подтвержден</Table.ColumnHeader>
                        <Table.ColumnHeader>Оплачен</Table.ColumnHeader>
                        <Table.ColumnHeader>Действия</Table.ColumnHeader>
                    </Table.Row>
                </Table.Header>
                <Table.Body>
                    {items.map((item) => (
                        <Table.Row key={item.ID}>
                            <Table.Cell>{item.NAME}</Table.Cell>
                            <Table.Cell>{item.TYPE1}</Table.Cell>
                            <Table.Cell>{item.START_DATE ? DateTime.fromMillis(item.START_DATE).toFormat('dd.MM.yyyy') : ""}</Table.Cell>
                            <Table.Cell>{item.END_DATE ? DateTime.fromMillis(item.END_DATE).toFormat('dd.MM.yyyy') : ""}</Table.Cell>
                            <Table.Cell>{item.VALUE1}</Table.Cell>
                            <Table.Cell>{item.VERSION ? DateTime.fromMillis(item.VERSION).toFormat('HH.mm dd.MM.yyyy') : ""}</Table.Cell>
                            <Table.Cell>{item.SENDED ? DateTime.fromMillis(item.SENDED).toFormat('HH.mm dd.MM.yyyy') : ""}</Table.Cell>
                            <Table.Cell>{item.ACCEPTED ? DateTime.fromMillis(item.ACCEPTED).toFormat('HH.mm dd.MM.yyyy') : ""}</Table.Cell>
                            <Table.Cell>{item.EXECUTED ? DateTime.fromMillis(item.EXECUTED).toFormat('HH.mm dd.MM.yyyy') : ""}</Table.Cell>
                            <Table.Cell>{item.CONFIRMED ? DateTime.fromMillis(item.CONFIRMED).toFormat('HH.mm dd.MM.yyyy') : ""}</Table.Cell>
                            <Table.Cell>{item.PAID ? DateTime.fromMillis(item.PAID).toFormat('HH.mm dd.MM.yyyy') : ""}</Table.Cell>
                            <Table.Cell>
                                <Button onClick={e => {
                                    if (showForm && inputParams && inputParams.id === item.ID) {
                                        prepareInput(null);
                                        setShowForm(false);
                                    } else {
                                        prepareInput(item);
                                        setShowForm(true);
                                    }
                                }}>{!item.SENDED && !item.CONFIRMED ? "Изменить" : "Показать"}</Button>
                                <Show when={props.isCompany}>
                                    <Button
                                        display={item.SENDED ? "none" : "block"}
                                        onClick={e => setSend(item)}>Отправить</Button>
                                    <Button
                                        display={!item.EXECUTED || item.CONFIRMED ? "none" : "block"}
                                        onClick={e => setConfirm(item, item.CALCULATION_TIME_H_ESTIMATED)}>Подтвердить</Button>
                                </Show>
                                <Show when={!props.isCompany}>
                                    <Button
                                        display={!item.ACCEPTED || item.EXECUTED ? "none" : "block"}
                                        onClick={e => setExecute(item, item.CALCULATION_TIME_H_ESTIMATED)}>Исполнить</Button>
                                </Show>
                                <Button
                                    display={item.SENDED ? "none" : "block"}
                                    onClick={e => remove(item)}>Удалить</Button>
                            </Table.Cell>
                        </Table.Row>
                    ))}
                </Table.Body>
            </Table.Root>
        </Box>
    ) : (<></>);
}